import type { CheckoutStep } from "~/features/checkout/checkout.schemas";
import { CHECKOUT_STEP_NAMES } from "~/features/checkout/constants";

/**
 * Ecommerce step titles for cannabis checkout.
 * One title per CTA / next view (not Measurement Plan's 6 buckets).
 * `/checkout/summary` is a single step — gated patient/address UI stays on-page only.
 */
export type TELEMETRY_CHECKOUT_STEP =
	| "prescription_choice"
	| "prescription_upload"
	| "diagnose_symptoms"
	| "diagnose_treatment"
	| "diagnose_cannabis_treatment"
	| "diagnose_therapy_effects"
	| "diagnose_exclusion_criteria"
	| "diagnose_different_medication"
	| "awareness"
	| "cross_sell_skip_and_continue_to_checkout"
	| "cross_sell_continue_to_checkout"
	| "delivery_package_1"
	| "delivery_package_2"
	| "summary"
	| "payment";

export type CheckoutTelemetryFlow = "initial" | "follow-up" | "upload";

/**
 * Which of the cross-sell step's two titles applies.
 *
 * The screen reports the state the customer acted in rather than the route it
 * sits on: its CTA reads "skip and continue to checkout" until the basket gains
 * an accessory and "continue to checkout" after. Analytics counts those
 * separately to see how many customers the step actually converts, so the view
 * and the completion both have to name the state they happened in.
 */
export function resolveCrossSellTelemetryStep(
	hasAccessories: boolean,
): TELEMETRY_CHECKOUT_STEP {
	return hasAccessories
		? "cross_sell_continue_to_checkout"
		: "cross_sell_skip_and_continue_to_checkout";
}

/**
 * The title for a delivery package at `position`, 1-based.
 *
 * The delivery step is one route but reports a step per package, because that is
 * where the customer makes a decision and where analytics measures drop-off.
 *
 * Null past the second package. A basket holds one group per *vendor* rather
 * than one per kind, so a second accessories vendor would make a third package
 * possible; today there is only one, and the analytics spec names exactly two.
 * Callers report the null rather than emitting a wrong title, so the assumption
 * surfaces the day it stops holding instead of silently mislabelling a package.
 */
export function resolveDeliveryPackageTelemetryStep(
	position: number,
): TELEMETRY_CHECKOUT_STEP | null {
	if (position === 1) {
		return "delivery_package_1";
	}
	if (position === 2) {
		return "delivery_package_2";
	}
	return null;
}

const SUMMARY_TAIL = [
	"summary",
	"payment",
] as const satisfies readonly TELEMETRY_CHECKOUT_STEP[];

// Appended after the tail rather than inserted where they occur, which is why
// registering them moves nothing: the number is indexOf + 1, so every existing
// step keeps the position it has reported since before OTC. The analytics spec
// asks for exactly this -- "let's not change the sequence of the step numbers,
// add new steps incrementally" -- because OTC ships to a percentage of cannabis
// users and both cohorts have to report the same number for the same step.
//
// The order within the block is the order a customer meets them, so the initial
// flow lands on 10-13 as the spec tabulates. The shorter questionnaires number
// them lower; the spec accepts that, since step_title is the key analytics reads.
const OTC_STEPS = [
	"cross_sell_skip_and_continue_to_checkout",
	"cross_sell_continue_to_checkout",
	"delivery_package_1",
	"delivery_package_2",
] as const satisfies readonly TELEMETRY_CHECKOUT_STEP[];

/** Initial questionnaire path (new prescription). */
export const INITIAL_SEQUENCE: readonly TELEMETRY_CHECKOUT_STEP[] = [
	"prescription_choice",
	"diagnose_symptoms",
	"diagnose_treatment",
	"diagnose_cannabis_treatment",
	"diagnose_exclusion_criteria",
	"diagnose_different_medication",
	"awareness",
	...SUMMARY_TAIL,
	...OTC_STEPS,
] as const;

/** Follow-up questionnaire path. */
export const FOLLOW_UP_SEQUENCE: readonly TELEMETRY_CHECKOUT_STEP[] = [
	"prescription_choice",
	"diagnose_therapy_effects",
	"diagnose_exclusion_criteria",
	"diagnose_different_medication",
	"awareness",
	...SUMMARY_TAIL,
	...OTC_STEPS,
] as const;

/** Upload existing prescription path. */
export const UPLOAD_SEQUENCE: readonly TELEMETRY_CHECKOUT_STEP[] = [
	"prescription_choice",
	"prescription_upload",
	...SUMMARY_TAIL,
	...OTC_STEPS,
] as const;

const FLOW_SEQUENCES: Record<
	CheckoutTelemetryFlow,
	readonly TELEMETRY_CHECKOUT_STEP[]
> = {
	initial: INITIAL_SEQUENCE,
	"follow-up": FOLLOW_UP_SEQUENCE,
	upload: UPLOAD_SEQUENCE,
};

export const CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP: Record<
	CheckoutStep,
	TELEMETRY_CHECKOUT_STEP
> = {
	[CHECKOUT_STEP_NAMES.prescriptionOrder]: "prescription_choice",
	[CHECKOUT_STEP_NAMES.prescriptionChoice]: "prescription_upload",
	[CHECKOUT_STEP_NAMES.diagnoseSymptoms]: "diagnose_symptoms",
	[CHECKOUT_STEP_NAMES.diagnoseTreatment]: "diagnose_treatment",
	[CHECKOUT_STEP_NAMES.diagnoseCannabisTreatment]:
		"diagnose_cannabis_treatment",
	[CHECKOUT_STEP_NAMES.diagnoseTherapyEffects]: "diagnose_therapy_effects",
	[CHECKOUT_STEP_NAMES.diagnoseExclusionCriteria]:
		"diagnose_exclusion_criteria",
	[CHECKOUT_STEP_NAMES.diagnoseDifferentMedication]:
		"diagnose_different_medication",
	[CHECKOUT_STEP_NAMES.awareness]: "awareness",
	// Route-level defaults. Both routes report a finer title than the route can
	// express -- cross-sell by whether the basket gained an accessory, delivery by
	// which package is open -- so their components pass telemetryStep explicitly
	// and these values stand in only for callers that map from the route alone.
	[CHECKOUT_STEP_NAMES.crossSell]: "cross_sell_skip_and_continue_to_checkout",
	[CHECKOUT_STEP_NAMES.delivery]: "delivery_package_1",
	[CHECKOUT_STEP_NAMES.summary]: "summary",
};

const DIAGNOSE_TELEMETRY_STEPS = new Set<TELEMETRY_CHECKOUT_STEP>([
	"diagnose_symptoms",
	"diagnose_treatment",
	"diagnose_cannabis_treatment",
	"diagnose_therapy_effects",
	"diagnose_exclusion_criteria",
	"diagnose_different_medication",
]);

export function isDiagnoseTelemetryStep(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
): boolean {
	return DIAGNOSE_TELEMETRY_STEPS.has(stepTitle);
}

/**
 * Resolves which funnel sequence applies for step numbering.
 */
export function resolveCheckoutTelemetryFlow(input: {
	prescriptionUpload?: boolean;
	prescriptionOrderType?: "initial" | "follow-up";
}): CheckoutTelemetryFlow {
	if (input.prescriptionUpload) {
		return "upload";
	}
	if (input.prescriptionOrderType === "follow-up") {
		return "follow-up";
	}
	return "initial";
}

/**
 * Resolves flow from checkout prescription payload (or defaults to initial).
 */
export function resolveCheckoutTelemetryFlowFromPrescription(
	prescription:
		| { prescriptionUpload: true }
		| {
				prescriptionUpload: false;
				prescriptionOrderType: "initial" | "follow-up";
		  }
		| null
		| undefined,
): CheckoutTelemetryFlow {
	if (!prescription) {
		return "initial";
	}
	if (prescription.prescriptionUpload) {
		return "upload";
	}
	return resolveCheckoutTelemetryFlow({
		prescriptionOrderType: prescription.prescriptionOrderType,
	});
}

/**
 * 1-based funnel position for analytics (prescriptions-style).
 * Falls back to sequence length + 1 if the title is not in the flow.
 */
export function getNormalizedCheckoutStepNumber(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
	flow: CheckoutTelemetryFlow,
): number {
	const sequence = FLOW_SEQUENCES[flow];
	const index = sequence.indexOf(stepTitle);
	if (index >= 0) {
		return index + 1;
	}
	return sequence.length + 1;
}

export function getCheckoutTelemetrySequence(
	flow: CheckoutTelemetryFlow,
): readonly TELEMETRY_CHECKOUT_STEP[] {
	return FLOW_SEQUENCES[flow];
}

const FLOW_STORAGE_PREFIX = "telemetry:checkout-flow:";

export function parseCheckoutTelemetryFlow(
	value: string | null | undefined,
): CheckoutTelemetryFlow | undefined {
	if (value === "initial" || value === "follow-up" || value === "upload") {
		return value;
	}
}

function flowStorageKey(id: string | number): string {
	return `${FLOW_STORAGE_PREFIX}${id}`;
}

/**
 * Persists funnel flow so payment telemetry can number steps after cart is gone.
 */
export function rememberCheckoutTelemetryFlow(
	id: string | number | null | undefined,
	flow: CheckoutTelemetryFlow,
): void {
	if (id === null || id === undefined || id === "") {
		return;
	}
	try {
		sessionStorage.setItem(flowStorageKey(id), flow);
	} catch {
		// Private mode / SecurityError — payment falls back to initial.
	}
}

/**
 * Reads the first stored flow among ids, else initial.
 */
export function readCheckoutTelemetryFlow(
	...ids: Array<string | number | null | undefined>
): CheckoutTelemetryFlow {
	for (const id of ids) {
		if (id === null || id === undefined || id === "") {
			continue;
		}
		try {
			const parsed = parseCheckoutTelemetryFlow(
				sessionStorage.getItem(flowStorageKey(id)),
			);
			if (parsed) {
				return parsed;
			}
		} catch {
			// ignore inaccessible sessionStorage
		}
	}
	return "initial";
}
